// CSV export helper with Arabic (UTF-8 BOM) support + unique-key customer utilities
export function downloadCSV(filename, headers, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[,"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    headers.map(esc).join(","),
    ...rows.map((r) => r.map(esc).join(",")),
  ].join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 400);
}

// Normalize a Saudi phone number: keep digits only, strip leading country code/zeros.
// Returns "" if empty or clearly invalid (<5 digits).
export function normPhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D+/g, "");
  if (digits.length < 5) return "";
  // Saudi: 9665XXXXXXXX → 5XXXXXXXX, 05XXXXXXXX → 5XXXXXXXX, 5XXXXXXXX → same
  if (digits.startsWith("966")) return digits.slice(3).replace(/^0+/, "");
  if (digits.startsWith("0")) return digits.replace(/^0+/, "");
  return digits;
}

// Deduplicate customers by phone when available, fallback to name.
// Keeps the record with the highest visits value.
export function dedupeCustomers(cst) {
  const seen = new Map();
  for (const c of cst || []) {
    const ph = normPhone(c.phone);
    const key = ph ? `ph:${ph}` : `nm:${(c.name || "").trim().toLowerCase()}`;
    const existing = seen.get(key);
    if (!existing || (c.visits || 0) > (existing.visits || 0)) {
      seen.set(key, { ...c, phone: ph || c.phone });
    }
  }
  return Array.from(seen.values());
}

// Build a fast lookup index of customers keyed by normalized phone AND normalized name.
// Used to cross-reference invoices back to the canonical customer record.
export function buildCustomerIndex(cst) {
  const byPhone = new Map();
  const byName = new Map();
  for (const c of cst || []) {
    const ph = normPhone(c.phone);
    if (ph) byPhone.set(ph, c);
    const n = (c.name || "").trim().toLowerCase();
    if (n) byName.set(n, c);
  }
  return { byPhone, byName };
}

// Given an invoice row (may contain phone and/or client name) return the customer record or null.
export function matchCustomer(invoice, index) {
  const ph = normPhone(invoice.phone);
  if (ph && index.byPhone.has(ph)) return index.byPhone.get(ph);
  const n = (invoice.client || "").trim().toLowerCase();
  if (n && index.byName.has(n)) return index.byName.get(n);
  return null;
}
